// Track icons currently being loaded per map instance to prevent race conditions
const loadingIcons = new WeakMap<mapboxgl.Map, Set<string>>();

export class IconMapGalia {
  private static readonly iconInUse = [
    'basic',
    'base',
    'square',
    'minus',
    'plus',
    'relay',
    'cube',
    'arrow-road'
  ];

  static createIcon(combineIconName: string): SVGElement {
    const parser = new DOMParser();
    switch (combineIconName) {
      case 'carret-right':
        return parser
          .parseFromString(icons.carretRight, 'text/html')
          .getElementsByTagName('svg')[0];
      default:
        return parser
          .parseFromString(icons.carretRight, 'text/html')
          .getElementsByTagName('svg')[0];
    }
  }

  /** Load only legacy icons. Marker icons are generated on-demand via ensureIconExists(). */
  static loadIcons(map: mapboxgl.Map): void {
    // Legacy icons (if still needed)
    IconMapGalia.iconInUse.forEach((element) => {
      let sdf: boolean;
      const icon = IconMapGalia.createIcon(element);
      let height: number, width: number;
      if (element === 'base' || element === 'square' || element === 'arrow-road') {
        width = 24;
        height = 34;
        sdf = true;
      } else {
        width = 24;
        height = 24;
        sdf = false;
      }
      addSVGImageToMap(map, element, sdf, icon, width, height);
    });
  }

  /**
   * Ensure an icon with the given type and color exists. If not, generate it on-demand.
   *
   * @param map - The Mapbox map instance
   * @param type - Icon type (basic, delivery, pickup, etc.)
   * @param color - Hex color value (e.g., '#4e4c4c')
   */
  static ensureIconExists(map: mapboxgl.Map, type: string, color: string): void {
    const iconName = `${type}-${color.replace('#', '')}`;

    // Check if icon already exists or is being loaded
    if (map.hasImage(iconName)) {
      return;
    }

    if (!loadingIcons.has(map)) {
      loadingIcons.set(map, new Set());
    }

    if (loadingIcons.get(map)!.has(iconName)) {
      return;
    }

    // Generate the icon based on type
    let svgString: string;
    let width = 50;
    let height = 50;

    switch (type) {
      case 'basic':
        svgString = createColoredMarkerSVG(color);
        break;
      case 'delivery':
        svgString = createColoredDeliveryMarkerSVG(color);
        break;
      case 'pickup':
        svgString = createColoredPickupMarkerSVG(color);
        break;
      case 'finish':
        svgString = createColoredFinishMarkerSVG(color);
        break;
      case 'start':
        svgString = createColoredStartMarkerSVG(color);
        break;
      case 'delivery-pickup':
        svgString = createColoredPickAndDeliverMarkerSVG(color);
        break;
      case 'arrow-road':
        svgString = createColoredRoadArrowMarkerSVG(color);
        width = 45;
        height = 48;
        break;
      case 'grouped':
        svgString = createColoredMarkerForGroupingSVG(color);
        break;
      default:
        svgString = createColoredMarkerSVG(color);
    }

    const svg = this.parseIconFromString(svgString);
    addSVGImageToMap(map, iconName, false, svg, width, height);
  }

  static parseIconFromString(iconString: string): SVGElement {
    const parser = new DOMParser();
    return parser.parseFromString(iconString, 'text/html').getElementsByTagName('svg')[0];
  }
}

function addSVGImageToMap(
  map: mapboxgl.Map,
  name: string,
  sdf: boolean,
  svg: SVGElement,
  width: number,
  height: number
): void {
  // Check if image already exists to prevent duplicate registration errors
  if (map.hasImage(name)) {
    return;
  }

  // Initialize loading set for this map if it doesn't exist
  if (!loadingIcons.has(map)) {
    loadingIcons.set(map, new Set());
  }

  const loadingSet = loadingIcons.get(map)!;

  // Check if this icon is already being loaded
  if (loadingSet.has(name)) {
    return;
  }

  // Mark icon as loading
  loadingSet.add(name);

  svg.setAttribute('width', width.toString());
  svg.setAttribute('height', height.toString());

  const svgMapPinIconAsString = new XMLSerializer().serializeToString(svg);
  const img: HTMLImageElement = new Image(width, height);
  img.onload = () => {
    map.addImage(name, img, {
      sdf: sdf
    });
    // Remove from loading set once complete
    loadingSet.delete(name);
  };
  img.src = 'data:image/svg+xml;base64,' + btoa(svgMapPinIconAsString);
}

// Svg icons
const icons = {
  carretRight: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"> <rect width="256" height="256" fill="none" stroke="none"></rect> <polygon points="96 48 176 128 96 208 96 48" opacity="0.2"></polygon> <polygon points="96 48 176 128 96 208 96 48" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"></polygon> </svg>`
};

// Colored SVG generation functions

function createColoredMarkerSVG(centerColor: string): string {
  return `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1" stdDeviation="3" flood-color="rgba(0,0,0,0.1)" />
    </filter>
  </defs>
  <g>
    <path d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z" fill="white" filter="url(#shadow)"/>
    <path d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z" fill="${centerColor}"/>
  </g>
</svg>`;
}

function createColoredRoadArrowMarkerSVG(centerColor: string): string {
  return `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 48">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1" stdDeviation="3" flood-color="rgba(0,0,0,0.1)" />
    </filter>
  </defs>
  <g>
    <path d="M40.2973 19.7696C43.9009 21.872 43.9009 27.128 40.2973 29.2304L11.1081 46.26C7.5045 48.3625 3 45.7344 3 41.5296V7.47039C3 3.26555 7.5045 0.637533 11.1081 2.73995L40.2973 19.7696Z" fill="white" filter="url(#shadow)"/>
    <path d="M36.5136 22.6078C37.9551 23.4488 37.9551 25.5512 36.5136 26.3922L10.5677 41.5296C9.12623 42.3706 7.32443 41.3194 7.32443 39.6374L7.32443 9.36259C7.32443 7.68065 9.12623 6.62943 10.5677 7.4704L36.5136 22.6078Z" fill="${centerColor}"/>
  </g>
</svg>`;
}

function createColoredFinishMarkerSVG(centerColor: string): string {
  return `
<svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1" stdDeviation="3" flood-color="rgba(0,0,0,0.1)" />
    </filter>
  </defs>
  <g>
    <path d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z" fill="white" filter="url(#shadow)"/>
    <path d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z" fill="${centerColor}"/>
    <path d="M25 19H31V14.6084C32.8206 15.6619 34.3371 17.1794 35.3906 19H31V25H37C37 27.1864 36.4123 29.2346 35.3906 31H31V35.3906C29.2346 36.4123 27.1864 37 25 37V31H19V35.3906C17.1796 34.3372 15.6628 32.8204 14.6094 31H19V25H13C13 22.8136 13.5877 20.7654 14.6094 19H19V14.6084C20.7653 13.5869 22.8137 13 25 13V19ZM25 31H31V25H25V31ZM19 25H25V19H19V25Z" fill="white"/>
  </g>
</svg>`;
}

function createColoredDeliveryMarkerSVG(centerColor: string): string {
  return `
<svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1" stdDeviation="3" flood-color="rgba(0,0,0,0.1)" />
    </filter>
  </defs>
  <g>
    <path d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z" fill="white" filter="url(#shadow)"/>
    <path d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z" fill="${centerColor}"/>
    <path d="M26.7998 22.7002C26.7998 22.9763 27.0237 23.2002 27.2998 23.2002H33.5C33.7761 23.2002 34 23.4241 34 23.7002V26.2998C34 26.5759 33.7761 26.7998 33.5 26.7998H27.2998C27.0237 26.7998 26.7998 27.0237 26.7998 27.2998V33.5C26.7998 33.7761 26.5759 34 26.2998 34H23.7002C23.4241 34 23.2002 33.7761 23.2002 33.5V27.2998C23.2002 27.0237 22.9763 26.7998 22.7002 26.7998H16.5C16.2239 26.7998 16 26.5759 16 26.2998V23.7002C16 23.4241 16.2239 23.2002 16.5 23.2002H22.7002C22.9763 23.2002 23.2002 22.9763 23.2002 22.7002V16.5C23.2002 16.2239 23.4241 16 23.7002 16H26.2998C26.5759 16 26.7998 16.2239 26.7998 16.5V22.7002Z" fill="white"/>
  </g>
</svg>`;
}

function createColoredPickupMarkerSVG(centerColor: string): string {
  return `
<svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1" stdDeviation="3" flood-color="rgba(0,0,0,0.1)" />
    </filter>
  </defs>
  <g>
    <path d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z" fill="white" filter="url(#shadow)"/>
    <path d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z" fill="${centerColor}"/>
    <rect x="16" y="23" width="18" height="4" rx="0.5" fill="white"/>
  </g>
</svg>`;
}

function createColoredPickAndDeliverMarkerSVG(centerColor: string): string {
  return `
<svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1" stdDeviation="3" flood-color="rgba(0,0,0,0.1)" />
    </filter>
  </defs>
  <g>
    <path d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z" fill="white" filter="url(#shadow)"/>
    <path d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z" fill="${centerColor}"/>
    <path d="M20.6826 14C20.9841 14.0001 21.2732 14.1265 21.4863 14.3516C21.6995 14.5766 21.8193 14.8819 21.8193 15.2002V18H34.5C34.7761 18 35 18.2239 35 18.5V21.5C35 21.7761 34.7761 22 34.5 22H21.8193V24.7998C21.8193 25.1181 21.6995 25.4234 21.4863 25.6484C21.2732 25.8735 20.9841 25.9999 20.6826 26C20.3812 26 20.0921 25.8734 19.8789 25.6484L15.333 20.8486C15.2276 20.7374 15.1441 20.6053 15.0869 20.46C15.0297 20.3143 15 20.1577 15 20C15 19.8423 15.0297 19.6857 15.0869 19.54C15.1441 19.3947 15.2276 19.2626 15.333 19.1514L19.8789 14.3516C20.0921 14.1266 20.3812 14 20.6826 14Z" fill="white"/>
    <path d="M29.3174 24C29.0159 24.0001 28.7268 24.1265 28.5137 24.3516C28.3005 24.5766 28.1807 24.8819 28.1807 25.2002V28H15.5C15.2239 28 15 28.2239 15 28.5V31.5C15 31.7761 15.2239 32 15.5 32H28.1807V34.7998C28.1807 35.1181 28.3005 35.4234 28.5137 35.6484C28.7268 35.8735 29.0159 35.9999 29.3174 36C29.6188 36 29.9079 35.8734 30.1211 35.6484L34.667 30.8486C34.7724 30.7374 34.8559 30.6053 34.9131 30.46C34.9703 30.3143 35 30.1577 35 30C35 29.8423 34.9703 29.6857 34.9131 29.54C34.8559 29.3947 34.7724 29.2626 34.667 29.1514L30.1211 24.3516C29.9079 24.1266 29.6188 24 29.3174 24Z" fill="white"/>
  </g>
</svg>`;
}

function createColoredStartMarkerSVG(centerColor: string): string {
  return `
  <svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1" stdDeviation="3" flood-color="rgba(0,0,0,0.1)" />
    </filter>
  </defs>
  <g>
    <path d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z" fill="white" filter="url(#shadow)"/>
    <path d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z" fill="${centerColor}"/>
    <path d="M34.5 24.134C35.1667 24.5189 35.1667 25.4811 34.5 25.866L21 33.6603C20.3333 34.0452 19.5 33.564 19.5 32.7942V17.2058C19.5 16.436 20.3333 15.9548 21 16.3397L34.5 24.134Z" fill="white"/>
  </g>
</svg>`;
}

function createColoredMarkerForGroupingSVG(outsideColor: string): string {
  return `
<svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="1" stdDeviation="3" flood-color="rgba(0,0,0,0.1)" />
    </filter>
  </defs>
  <g>
    <path d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z" fill="${outsideColor}" filter="url(#shadow)" />
    <path d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z" fill="white"/>
  </g>
</svg>`;
}
